import React, { useState, useRef, useEffect, useContext } from 'react';
import styles from './AppleLanding.module.css';
import { Container, Row, Col } from 'reactstrap';
import classNames from 'classnames';
import getClientContent from '../_BrandingProvider/brandContent';
import { ReactComponent as CheckMark } from '../../assets/images/checkmark.svg';
import { ReactComponent as FileIcon } from '../../assets/images/Vendor/Apple/ViewFile.svg';
import DevicesImage from '../../assets/images/Vendor/Apple/iPhone-iPad-iWatch.png';
import AppleCareImage from '../../assets/images/Vendor/Apple/AppleCare+.png';
import WhatsCoveredModal from '../modals/WhatsCoveredModal';
import { useHistory } from 'react-router-dom';
import { SalesContext } from '../contexts/SalesContext';
import { BrandingContext } from '../contexts/ContractContext';
import useLoadAtTop from '../_ReactHooks/useLoadAtTop';

const PlanCard = ({ cardTitle, paragraph1, toggleOpen, allowQuote = false, pricingAmount = null }) => {
    const history = useHistory();
    const handleNavigation = (title) => {
        history.push({
            pathname: '/buy/protectionplan',
            state: {
                selectedPlan: title
            }
        });
    };

    return (
        <div className={styles.planCard}>
            <h4 className='mb-0'>{cardTitle}</h4>
            <p>Standalone Theft and Loss Protection</p>
            <p>{paragraph1}</p>
            <p>
                Requires{' '}
                <a
                    href='https://www.apple.com/icloud/find-my/'
                    target='_blank'
                >
                    Find My
                </a>{' '}
                to be enabled on the device at the time it is lost or stolen and throughout the claims process.
            </p>
            <a
                onClick={() => toggleOpen(true)}
                className={styles.whatsCoveredLink}
            >
                <FileIcon /> View What's Covered
            </a>
            <hr />
            {allowQuote ? (
                <div className={styles.getQuoteContainer}>
                    <button
                        className='btn-primary'
                        onClick={() => handleNavigation(cardTitle)}
                    >
                        Get a Quote
                    </button>
                </div>
            ) : (
                <div className={styles.buyNowContainer}>
                    <h3>${pricingAmount?.toFixed(2)}</h3>
                    <button
                        className='btn-primary'
                        onClick={() => handleNavigation(cardTitle)}
                    >
                        Buy Now
                    </button>
                </div>
            )}
        </div>
    );
};

export default function AppleLanding() {
    useLoadAtTop();
    const context = useContext(SalesContext);
    const branding = useContext(BrandingContext);
    const clientId = branding.clientId || 'AIG';
    const clientLower = clientId?.toLowerCase();
    const [isOpen, setIsOpen] = useState(false);
    const [multiDevicePlanPrice, setMultiDevicePlanPrice] = useState(null);
    const targetElementRef = useRef(null);
    const { variableText } = getClientContent(clientId);

    const scrollToElement = () => {
        if (targetElementRef.current) {
            targetElementRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const resetContext = {
        contract: {},
        deviceData: {
            activePlan: {},
            deviceInfo: [],
            planPrice: null,
            selectedPlan: '',
            zip: '',
            taxAmount: null
        },
        formData: {
            invitationNumber: '0',
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            serviceAddress: {
                address1: '',
                address2: '',
                city: '',
                state: 'NJ',
                zip: '',
                stateId: 39
            },
            mailingAddress: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                stateId: ''
            },
            billingAddress: {
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                stateId: ''
            },
            doNotSolicit: null,
            serviceAsMailingAddress: true,
            billingAsServiceAddress: true
        }
    };

    const handleResetContext = () => {
        context.updateContract(resetContext.contract);
        context.updateDeviceData(resetContext.deviceData);
        context.updateFormData(resetContext.formData);
    };

    const getPricingAmount = (riskData) => {
        //Logic to pull pricing amount to be updated here, only multiDevice has viewable pricing at the moment
        const multiDevicePricingInfo = riskData?.find(
            (profile) => profile?.riskProfileName === 'Multi-device Plan (2-year)'
        );
        setMultiDevicePlanPrice(multiDevicePricingInfo?.pricingInfo[0]?.amount || null);
    };

    const getRiskProfileData = async () => {
        fetch(`api/pricing/unbundle/riskprofiles/us/${clientLower}`)
            .then((res) => res.json())
            .then((data) => {
                getPricingAmount(data);
            });
    };

    useEffect(() => {
        //Update risk profile data if clientId updates
        getRiskProfileData();
    }, [clientId]);

    useEffect(() => {
        handleResetContext();
        window.addEventListener('pagehide', handleResetContext);

        return () => {
            window.removeEventListener('pagehide', handleResetContext);
        };
    }, []);

    return (
        <>
            <div className={styles.sectionColorContainer}>
                <Container className={styles.landingContainer}>
                    <Row className={styles.standaloneContainer}>
                        <Col
                            md='12'
                            lg='6'
                            className={styles.standaloneInfo}
                        >
                            <h4>AIG Standalone Protection</h4>
                            <h3>Are your Apple devices partially protected?</h3>
                            <p>
                                Standalone coverage is available if your existing service plan leaves you vulnerable to
                                theft and loss.
                            </p>
                            <button
                                className={classNames(styles.shopButton, 'btn-primary')}
                                onClick={scrollToElement}
                            >
                                Shop Plans
                            </button>
                        </Col>
                        <Col
                            md='12'
                            lg='6'
                            className={styles.standaloneImage}
                        >
                            <img
                                src={DevicesImage}
                                alt='Device Image'
                            />
                        </Col>
                    </Row>
                </Container>
            </div>
            <Container>
                <Row>
                    <Col className={styles.standaloneDescriptionContainer}>
                        <h2>How does it work?</h2>
                        <p>AIG Standalone Protection covers you in the event your device is lost or stolen.</p>
                        <ul>
                            <li>
                                <CheckMark />
                                Devices enrolled in a multi-device plan must maintain primary coverage through an active
                                service plan with any provider.{' '}
                            </li>
                            <li>
                                <CheckMark />
                                Each claim is subject to a deductible, deductible amount varies by device.
                            </li>
                            <li>
                                <CheckMark />
                                To file a theft or loss claim call {variableText?.claim?.phone}.
                            </li>
                        </ul>
                    </Col>
                </Row>
            </Container>
            <div
                className={styles.sectionColorContainer}
                ref={targetElementRef}
            >
                <Container>
                    <Row>
                        <Col className={styles.plansTitleContainer}>
                            <h3>Choose Your Plan</h3>
                            <p>Single or multi-device plans are available.</p>
                        </Col>
                    </Row>
                    <Row className={styles.planCardContainer}>
                        <Col
                            md='12'
                            lg='6'
                            className={styles.planCardColContainer}
                        >
                            <PlanCard
                                allowQuote={true}
                                cardTitle={'Single Device Plan (2-year)'}
                                paragraph1={
                                    'Covers up to two claims of theft or loss on one device within a 12-month period, deductible applies.'
                                }
                                toggleOpen={setIsOpen}
                            />
                        </Col>
                        <Col
                            md='12'
                            lg='6'
                            className={styles.planCardColContainer}
                        >
                            <PlanCard
                                cardTitle={'Multi-device Plan (2-year)'}
                                paragraph1={
                                    'Covers up to three claims of theft or loss on three devices within a 12-month period, deductible applies.'
                                }
                                toggleOpen={setIsOpen}
                                pricingAmount={multiDevicePlanPrice}
                            />
                        </Col>
                    </Row>
                </Container>
            </div>
            <Container>
                <Row>
                    <Col className={styles.appleCareProductsContainer}>
                        <img
                            src={AppleCareImage}
                            alt='AppleCare+'
                        />
                        <div>
                            <h4>
                                AppleCare <span>Products</span>
                            </h4>
                            <p>
                                Do not have an existing service plan or looking for full coverage?
                                <a
                                    href='https://www.apple.com/applecare/'
                                    target='_blank'
                                >
                                    {' '}
                                    Shop AppleCare
                                </a>
                            </p>
                        </div>
                    </Col>
                </Row>
            </Container>
            {isOpen && (
                <WhatsCoveredModal
                    isOpen={isOpen}
                    onCloseModal={() => setIsOpen(false)}
                />
            )}
        </>
    );
}
